import type { IframeUnopenedOffer } from '../../../modules/api';
import { UnopenedPackCard } from '../cards/UnopenedPackCard';
import { EmptyState } from '../EmptyState';

export interface RepackzTabProps {
  offers: IframeUnopenedOffer[];
  /** First-ever load of this feed — the only state that gets a skeleton. A
   *  reload behind an already-rendered tab must not, see useInfinitePagination. */
  initialLoading: boolean;
  loadingMore: boolean;
  reachedEnd: boolean;
  /** Sentinel ref from useInfinitePagination — attach to a node at the
   *  end of the grid to drive infinite scroll. */
  sentinelRef: (node: Element | null) => void;
  onOpen: (offer: IframeUnopenedOffer) => void;
  onBrowse: () => void;
}

const SKELETON_COUNT = 3;

function PackSkeleton() {
  return (
    <div
      data-co-component="MyPacksSkeleton"
      className="aspect-[485/311] rounded-[28px] animate-pulse"
      style={{
        background: 'var(--co-MyPacksSkeleton-fill, rgba(255,255,255,0.05))',
        borderStyle: 'solid',
        borderColor: 'var(--co-MyPacksSkeleton-borderColor, rgba(171,174,179,0.24))',
        borderWidth: 'var(--co-MyPacksSkeleton-borderWidth, 2px)',
      }}
    />
  );
}

export function RepackzTab({
  offers,
  initialLoading,
  loadingMore,
  reachedEnd,
  sentinelRef,
  onOpen,
  onBrowse,
}: RepackzTabProps) {
  if (initialLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 @[600px]:grid-cols-2 @[900px]:grid-cols-3">
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <PackSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <EmptyState
        title="No Packs Found"
        cta="Buy Packs"
        onAction={onBrowse}
      />
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 @[600px]:grid-cols-2 @[900px]:grid-cols-3">
        {offers.map((offer) => (
          <UnopenedPackCard key={offer.id} offer={offer} onOpen={onOpen} />
        ))}
        {loadingMore &&
          Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <PackSkeleton key={`sk-${i}`} />
          ))}
      </div>
      {!reachedEnd && <div ref={sentinelRef} aria-hidden className="h-px" />}
    </>
  );
}
